import { useEffect, useState } from 'react';
import { PostgrestError, PostgrestSingleResponse } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';

// Logger utility for API responses
const apiLogger = {
  log: (message: string, data?: any) => {
    console.log(`%c[API Logger] ${message}`, 'color: #3498db; font-weight: bold;', data || '');
  },
  error: (message: string, error?: any) => {
    console.error(`%c[API Logger ERROR] ${message}`, 'color: #e74c3c; font-weight: bold;', error || '');
  },
  debug: (message: string, data?: any) => {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`%c[API Logger DEBUG] ${message}`, 'color: #2ecc71; font-weight: bold;', data || '');
    }
  }
};

type TableName = keyof Database['public']['Tables'];
type Row<T extends TableName> = Database['public']['Tables'][T]['Row'];

export function useSupabaseQuery<T extends TableName>(
  tableName: T,
  options: {
    select?: string;
    filter?: Record<string, any>;
    orderBy?: { column: string; ascending?: boolean };
    limit?: number;
    single?: boolean;
    enableLogging?: boolean;
  } = {}
) {
  const [data, setData] = useState<Row<T>[] | null>(null);
  const [error, setError] = useState<PostgrestError | null>(null);
  const [loading, setLoading] = useState(true);
  const [rawResponse, setRawResponse] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      const isLoggingEnabled = options.enableLogging ?? true;
      
      if (isLoggingEnabled) {
        apiLogger.debug(`Starting query for ${tableName}`, {
          tableName,
          options
        });
      }
      
      try {
        let query = supabase.from(tableName).select(options.select || '*');

        if (options.filter) {
          Object.entries(options.filter).forEach(([key, value]) => {
            query = query.eq(key, value);
          });
        }

        if (options.orderBy) {
          query = query.order(options.orderBy.column, {
            ascending: options.orderBy.ascending ?? true,
          });
        }

        if (options.limit) {
          query = query.limit(options.limit);
        }

        let response;
        
        if (options.single) {
          response = await query.single() as PostgrestSingleResponse<Row<T>>;
          const { data: result, error: queryError } = response;
          
          if (isLoggingEnabled) {
            setRawResponse(response);
            apiLogger.log(`Response from ${tableName} (single):`, response);
          }
          
          if (queryError) throw queryError;
          setData(result ? [result] : null);
        } else {
          response = await query as PostgrestSingleResponse<Row<T>[]>;
          const { data: result, error: queryError } = response;
          
          if (isLoggingEnabled) {
            setRawResponse(response);
            apiLogger.log(`Response from ${tableName}:`, response);
            
            // Log data structure and first item for analysis
            if (result && result.length > 0) {
              apiLogger.debug(`Data structure for ${tableName}`, {
                count: result.length,
                keys: Object.keys(result[0]),
                sample: result[0]
              });
            } else {
              apiLogger.debug(`Empty result set for ${tableName}`); 
            }
          }
          
          if (queryError) throw queryError;
          setData(result);
        }
      } catch (err) {
        if (isLoggingEnabled) {
          apiLogger.error(`Error querying ${tableName}:`, err);
        }
        setError(err as PostgrestError);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tableName, options.select, options.filter, options.orderBy, options.limit, options.single, options.enableLogging]);

  return { data, error, loading, rawResponse };
}